import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | XtraGlass';
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a0b]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <motion.a
            href="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            whileHover={{ x: -4 }}
          >
            <ArrowLeft size={20} />
            <span className="font-mono text-sm">Back to Home</span>
          </motion.a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            Privacy Policy
          </h1>
          <p className="text-white/50 mb-12">Last Updated: October 8, 2023</p>

          <div className="prose prose-invert prose-lg max-w-none">
            <div className="space-y-8 text-white/70">
              <p>
                This Privacy Policy describes the practices of XtraGlass regarding the collection, use, disclosure, and protection of your personal information when you use our Nightlife Discovery mobile application. We are committed to protecting your privacy and ensuring that any personal information that you provide us is handled securely and in accordance with applicable laws and regulations.
              </p>

              <p>
                When you use XtraGlass, you&apos;re trusting us to handle your personal information with care. We take that trust seriously. This Privacy Policy describes how we collect, use, and protect your personal information.
              </p>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6 my-8">
                <h3 className="text-xl font-display font-bold text-white mb-4">Key Points:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-[#ff84e4]">1.</span>
                    We don&apos;t and will not sell your data.
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#ff84e4]">2.</span>
                    We offer a free service for the outgoing audience and hope you&apos;ll purchase one of our paid products. That&apos;s how we make money.
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#ff84e4]">3.</span>
                    When you sign up for and use our service, we process your personal information to provide you with suggestions or to provide you with an optimized service.
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#ff84e4]">4.</span>
                    When you use an XtraGlass account, your use of our products is governed by XtraGlass&apos;s organizational policies.
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#ff84e4]">5.</span>
                    We may use a small number of trusted third parties to help provide our services.
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#ff84e4]">6.</span>
                    By accessing or using our App, you agree to the terms of this Privacy Policy.
                  </li>
                </ul>
              </div>

              <h2 className="text-2xl font-display font-bold text-white mt-12">1. Information We Collect</h2>
              
              <h3 className="text-xl font-display font-semibold text-white">1.1 Personal Information</h3>
              <p>When you use our App, we may collect personal information that you provide to us voluntarily. This may include:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Contact information (such as your name, email address, and phone number)</li>
                <li>Profile information (such as your username, profile picture, and preferences)</li>
                <li>Location information (such as your current location or the location you search for)</li>
                <li>Payment information (such as credit card details if you make in-app purchases)</li>
                <li>Communication history between you and other users or with us</li>
              </ul>

              <h3 className="text-xl font-display font-semibold text-white">1.2 Usage Information</h3>
              <p>We may automatically collect certain information about how you use our App, such as:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Device information (including device type, operating system, unique device identifiers)</li>
                <li>Log information (such as IP addresses, app crashes, and system activity)</li>
                <li>Usage patterns (such as the features you interact with, time and duration of your visits, and search queries)</li>
              </ul>

              <h2 className="text-2xl font-display font-bold text-white mt-12">2. How We Use Your Information</h2>
              
              <h3 className="text-xl font-display font-semibold text-white">2.1 Providing and Improving Our Services</h3>
              <p>We use your personal information to provide you with the requested services and improve our App&apos;s functionality. This includes:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Facilitating your interactions with other users</li>
                <li>Customizing your user experience</li>
                <li>Analyzing usage patterns to enhance our App&apos;s performance</li>
                <li>Developing new features and services</li>
                <li>Responding to your inquiries and support requests</li>
              </ul>

              <h2 className="text-2xl font-display font-bold text-white mt-12">3. Data Security</h2>
              <p>
                We take reasonable measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. However, no data transmission or storage system can guarantee 100% security. Therefore, we strive to the best of our ability to protect your data.
              </p>

              <h2 className="text-2xl font-display font-bold text-white mt-12">Contact Us</h2>
              <p>If you have any questions about this Privacy Policy, You can contact us:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>By email: <a href="mailto:xtraglassindia@gmail.com" className="text-[#ff84e4] hover:underline">xtraglassindia@gmail.com</a></li>
                <li>Website: <a href="https://www.xtraglass.com" className="text-[#ff84e4] hover:underline" target="_blank" rel="noopener noreferrer">www.xtraglass.com</a></li>
              </ul>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-white/40 text-sm">
          © {new Date().getFullYear()} XtraGlass. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;